// ** React Import
import { useEffect, useRef, useState } from 'react'

// ** Full Calendar & it's Plugins
import FullCalendar from '@fullcalendar/react'
import listPlugin from '@fullcalendar/list'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import bootstrap5Plugin from '@fullcalendar/bootstrap5'
import interactionPlugin from '@fullcalendar/interaction'
import { useDispatchBoardPermission } from "screens/DispatchBoard/permissions/DispatchBoardPermissions";

// ** Third Party Style Import
import 'bootstrap-icons/font/bootstrap-icons.css'

const blankEvent = {
  title: '',
  start: '',
  end: '',
  allDay: false,
  url: '',
  extendedProps: {
    calendar: '',
    guests: [],
    location: '',
    description: ''
  }
}

const Calendar = props => {
  // ** Props
  const {
    store,
    dispatch,
    direction,
    updateEvent,
    calendarApi,
    calendarsColor,
    setCalendarApi,
    handleSelectEvent,
    handleLeftSidebarToggle,
    handleAddEventSidebarToggle,
    appliedFilters,
    setCalendarViewType,
  } = props
   const { DispatchBoardPermissionProps } = useDispatchBoardPermission();
    const {
      hasDbGetPermission,
      hasDbSavePermission
    } = DispatchBoardPermissionProps;

  // ** Refs
  const calendarRef = useRef()
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const [filteredEventsForView, setFilteredEventsForView] = useState([])
  
  useEffect(() => {
    if (calendarApi === null) {
      // @ts-ignore
      setCalendarApi(calendarRef.current?.getApi())
    }
  }, [calendarApi, setCalendarApi])
  
  // Filter events and handle view switching
  useEffect(() => {
    if (!store) return;
    
    // Filter events based on appliedFilters
    let filteredEvents = store.events.length ? [...store.events] : [];
    
    const filtersActive = 
      (appliedFilters?.statuses?.length > 0) ||
      (appliedFilters?.services?.length > 0) ||
      (appliedFilters?.industries?.length > 0);
    
    if (appliedFilters && filtersActive) {
      filteredEvents = filteredEvents.filter(event => {
        const props = event.extendedProps || {};
        
        // Apply industry filter - by name
        if (appliedFilters.industries?.length > 0) {
          const industryMatch = appliedFilters.industries.some(
            industryName => props.industryName === industryName
          );
          if (!industryMatch) return false;
        }
        
        // Apply service filter - by name
        if (appliedFilters.services?.length > 0) {
          const serviceMatch = appliedFilters.services.some(
            serviceName => props.subServiceName === serviceName
          );
          if (!serviceMatch) return false;
        }
        
        // Apply status filter
        if (appliedFilters.statuses?.length > 0) {
          const eventStatus = props.status || 'Unscheduled';
          const statusMatch = appliedFilters.statuses.some(
            status => {
              // Handle null/undefined status
              if (!status) return false;
              if (!eventStatus) return status.toLowerCase() === 'unscheduled';
              return status.toLowerCase() === eventStatus.toLowerCase();
            }
          );
          if (!statusMatch) return false;
        }
        
        return true;
      });
    }
    
    setHasActiveFilters(filtersActive);
    setFilteredEventsForView(filteredEvents);
    
    // Get unique statuses from all events for debugging
    const uniqueStatuses = [...new Set(store.events.map(e => e.extendedProps?.status))];
    
    // Switch calendar view based on filters
    if (calendarApi) {
      if (filtersActive) {
        // Switch to list view when filters are active
        if (filteredEvents.length > 0) {
          // Find the earliest event date
          const earliestEvent = filteredEvents.reduce((earliest, event) => {
            const eventDate = new Date(event.start);
            const earliestDate = earliest ? new Date(earliest.start) : null;
            return !earliestDate || eventDate < earliestDate ? event : earliest;
          }, null);
          
          if (earliestEvent && earliestEvent.start) {
            // Go to the earliest event date
            calendarApi.gotoDate(earliestEvent.start);
          }
        }
        
        // Remove all existing events and add filtered ones
        const allEvents = calendarApi.getEvents();
        allEvents.forEach(event => event.remove());
        
        // Add filtered events
        filteredEvents.forEach(event => {
          calendarApi.addEvent(event);
        });
        
        calendarApi.changeView('listMonth');
      } else {
        // Switch back to month view when filters are cleared
        // Remove all events and re-add from store
        const allEvents = calendarApi.getEvents();
        allEvents.forEach(event => event.remove());
        
        // Add all events back
        if (store.events.length > 0) {
          store.events.forEach(event => {
            calendarApi.addEvent(event);
          });
        }
        
        calendarApi.changeView('dayGridMonth');
        calendarApi.today();
      }
    }
  }, [store, appliedFilters, calendarApi])
  
  if (store) {
    // Use the filtered events from state
    const filteredEvents = filteredEventsForView.length > 0 || hasActiveFilters 
      ? filteredEventsForView 
      : (store.events.length ? store.events : [])
    
    // ** calendarOptions(Props)
    const calendarOptions = {
      events: filteredEvents,
      eventSources: [],  // Clear any event sources
      plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin, bootstrap5Plugin],
      initialView: 'dayGridMonth',
      eventDisplay: 'auto',  // Ensure events are displayed
      
      headerToolbar: {
        start: 'sidebarToggle, title',  // Removed prev, next arrows (now in Topbar filter)
        end: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
      },
      views: {
        week: {
          titleFormat: { year: 'numeric', month: 'long', day: 'numeric' }
        },
        listMonth: {
          // Show more events in list view when filters are active (6 months)
          duration: hasActiveFilters ? { months: 6 } : { months: 1 },
          titleFormat: hasActiveFilters 
            ? { year: 'numeric' } 
            : { year: 'numeric', month: 'long' },
          listDayFormat: { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }
        }
      },
      

      /*
            Enable dragging and resizing event
            ? Docs: https://fullcalendar.io/docs/editable
          */
      editable: true,

      /*
            Enable resizing event from start
            ? Docs: https://fullcalendar.io/docs/eventResizableFromStart
          */
      eventResizableFromStart: true,

      /*
              Automatically scroll the scroll-containers during event drag-and-drop and date selecting
              ? Docs: https://fullcalendar.io/docs/dragScroll
            */
      dragScroll: true,
       slotEventOverlap: true,


      /*
              Max number of events within a given day
              ? Docs: https://fullcalendar.io/docs/dayMaxEvents
            */
      dayMaxEvents: 2,
      

      /*
              Determines if day names and week names are clickable
              ? Docs: https://fullcalendar.io/docs/navLinks
            */
      navLinks: true,
      eventClassNames({ event: calendarEvent }) {
        // @ts-ignore
        const colorName = calendarsColor[calendarEvent._def.extendedProps.calendar]

        return [
          // Background Color
          `bg-${colorName}`
        ]
      },
      // eventClick({ event: clickedEvent }) {
      //   dispatch(handleSelectEvent(clickedEvent))
      //   handleAddEventSidebarToggle()

      //   // * Only grab required field otherwise it goes in infinity loop
      //   // ! Always grab all fields rendered by form (even if it get `undefined`) otherwise due to Vue3/Composition API you might get: "object is not extensible"
      //   // event.value = grabEventDataFromEventApi(clickedEvent)
      //   // isAddNewEventSidebarActive.value = true
      // },
      eventClick({ event }) {
        // this gives you { id, title, start, end, extendedProps: { ů } }
        const plain = event.toPlainObject()
        dispatch(handleSelectEvent(plain))
        // handleAddEventSidebarToggle()
        if (plain.extendedProps.calendar === "Job") {
          // console.log("Job Event");
          // console.log(plain.extendedProps)
          if (props.handleJobPopupOpen) {
            props.handleJobPopupOpen(plain);
          }
        } else {
          // For Assessment or other types, use the existing sidebar
          handleAddEventSidebarToggle();
        }
      },
      customButtons: {
        sidebarToggle: {
          icon: 'bi bi-list',
          click() {
            handleLeftSidebarToggle()
          }
        }
      },
    dateClick(info) {
  // Check permission before opening sidebar
  if (hasDbSavePermission) {
    dispatch(handleSelectEvent(null))
    handleAddEventSidebarToggle()
  } else {
    return false;
  }
},
      
      // Track view changes
      viewDidMount(info) {
        if (setCalendarViewType) {
          setCalendarViewType(info.view.type);
        }
      },

/*
        Handle event drop (Also include dragged event)
        ? Docs: https://fullcalendar.io/docs/eventDrop
        ? We can use `eventDragStop` but it doesn't return updated event so we have to use `eventDrop` which returns updated event
      */
      eventDrop({ event }) {
          // turn the EventApi into a plain object
          const ev = event.toPlainObject()
        dispatch(updateEvent(ev))
      },

/*
        Handle event resize
        ? Docs: https://fullcalendar.io/docs/eventResize
      */
      eventResize({ event }) {
        const ev = event.toPlainObject()
        dispatch(updateEvent(ev))
      },
      ref: calendarRef,

      // Get direction from app state (store)
      direction
    }

    // @ts-ignore
    return <FullCalendar {...calendarOptions} />
  } else {
    return null
  }
}

export default Calendar